import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  useFieldArray,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { CalendarIcon } from '../../../../../assets/icons/index';
import { AnimatedNumber } from '../../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../../../../components/button/index';
import { OverlayDatePicker as DatePicker } from '../../../../../components/date-picker/index';
import { DropDown } from '../../../../../components/dropdown/index';
import { FormInput } from '../../../../../components/input/index';
import { SharePlan, useFormat, useLocale, useSelectedCompany } from '../../../../../hooks/index';
import { EventFormType, EventUnionWithPermissions } from '../../../../../types/events.types';
import { toNumber } from '../../../../../utils/to-number.util';
import { toPositive } from '../../../../../utils/to-positive.util';
import { FormDropDown, StakeholderCombobox, SubEventFileUpload } from '../../components/index';
import { FormSchema, GrantSignatureOption } from '../../event-form.validation';
import { resolveTransactionItemDocusignFileLinks } from '../../utils/resolve-linked-signature-file-links.util';

export type GrantStepSecondProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  errors: FieldErrors<FormSchema>;
  event: EventUnionWithPermissions | null;
  formData: UseFormGetValues<FormSchema>;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

type GrantItemProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  errors: FieldErrors<FormSchema>;
  event: EventUnionWithPermissions | null;
  field: FieldArrayWithId<FormSchema>;
  firstDuplicateIndex: null | number;
  formData: UseFormGetValues<FormSchema>;
  getFullTitle: (index: number) => string;
  handleRemoveGrantItems: (index: number) => void;
  hasDuplicates: boolean;
  index: number;
  isShownRemoveButton: boolean;
  itemRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  scrollToFirstDuplicate: () => void;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};
const GrantItem: FC<GrantItemProps> = ({
  control,
  field,
  handleRemoveGrantItems,
  formData,
  setFormData,
  getFullTitle,
  index,
  isShownRemoveButton,
  companyId,
  firstDuplicateIndex,
  hasDuplicates,
  itemRefs,
  scrollToFirstDuplicate,
  setError,
  clearErrors,
  errors: { stepOne: errors },
  event,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const transactionId = stepOne?.eventDetails?.grant?.grantItems?.[index]?.id;
  const linkedDocusignFileLinks = resolveTransactionItemDocusignFileLinks(event, transactionId);
  const grantSharePlan = stepOne?.eventDetails?.grant?.sharePlan;
  const equityPlanId = grantSharePlan?.id ?? '';
  const sharePlan = stepOne?.eventDetails?.grant?.grantItems?.[index]?.sharePlan;
  const sharePlanId = sharePlan?.id ?? equityPlanId ?? '';
  const { format } = useFormat();
  const { sharePlan: sharePlanDetails } = SharePlan.useSharePlan({
    companyId,
    planId: sharePlanId,
  });

  const initialShares =
    stepOne?.eventDetails?.grant?.grantItems?.[index]?.initialShares || undefined;
  const initialSharePlanId =
    stepOne?.eventDetails?.grant?.grantItems?.[index]?.initialSharePlanId || undefined;
  const shouldAddInitialShares = initialShares && initialSharePlanId === sharePlanId;

  const poolId = sharePlan?.pool?.id ?? sharePlanDetails?.pool?.id;
  const poolTotalInitialShares = toNumber(
    stepOne?.eventDetails?.grant?.grantItems
      ?.filter((item) => item?.sharePlan?.pool?.id === poolId)
      .reduce((acc, { initialShares }) => acc + toNumber(initialShares), 0),
  );

  const poolTotalUsedShares = toNumber(
    stepOne?.eventDetails?.grant?.grantItems
      ?.filter((item) => item?.sharePlan?.pool?.id === poolId)
      .reduce((acc, { shares }) => acc + toNumber(shares), 0),
  );

  const pool = sharePlanDetails?.pool;
  const poolAvailable = shouldAddInitialShares
    ? toNumber(pool?.dillutedSharesCount) - toNumber(pool?.granted) + poolTotalInitialShares
    : toNumber(pool?.dillutedSharesCount) - toNumber(pool?.granted);

  const available = poolAvailable - poolTotalUsedShares;

  useEffect(() => {
    if (poolAvailable) {
      setFormData(`stepOne.eventDetails.grant.grantItems.${index}.balance`, Number(poolAvailable));
    }
  }, [index, poolAvailable, setFormData]);

  return (
    <>
      <FormDropDown
        handleRemove={() => handleRemoveGrantItems(index)}
        key={field.id}
        showRemoveButton={isShownRemoveButton}
        title={getFullTitle(index)}
      >
        <div className="flex w-full flex-col gap-4">
          <div
            className="flex w-full flex-col gap-3"
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
          >
            <div className="flex w-full items-center justify-between">
              <span className="text-sm font-[450] text-gray-700">
                <AppFormattedMessage id="GRANT_DATE" />
              </span>
              <DatePicker
                onSelect={(date) =>
                  setFormData(`stepOne.eventDetails.grant.grantItems.${index}.grantDate`, date)
                }
                value={stepOne?.eventDetails?.grant?.grantItems?.[index]?.grantDate}
                wrapperClassName="w-[224px]"
              >
                <FormInput
                  autoComplete="off"
                  control={control}
                  disabledInput
                  icon={<CalendarIcon className="mr-2 size-6" iconColor="#98A2B3" />}
                  name={`stepOne.eventDetails.grant.grantItems.${index}.grantDate`}
                  placeholder={<AppFormattedMessage id="DATE" />}
                  transform={(value) => {
                    return value && new Date(value).getTime() !== new Date(+0).getTime()
                      ? format(value, 'dd/MM/yyyy')
                      : '';
                  }}
                  wrapperClassName="w-full"
                />
              </DatePicker>
            </div>
            <div className="flex w-full items-center justify-between">
              <span className="text-sm font-[450] text-gray-700">
                <AppFormattedMessage id="STAKEHOLDER" />
              </span>
              <StakeholderCombobox
                companyId={companyId}
                control={control}
                name={`stepOne.eventDetails.grant.grantItems.${index}.stakeholder`}
              />
            </div>
            <div className="flex w-full items-center justify-between">
              <span className="text-sm font-[450] text-gray-700">
                <AppFormattedMessage id="NO_OF_SHARES" />
              </span>
              <div className="flex flex-col items-start gap-2">
                <FormInput
                  control={control}
                  errorMessage={available < 0 && 'Grant amount exceeds available pool'}
                  name={`stepOne.eventDetails.grant.grantItems.${index}.shares`}
                  numberOnly
                  placeholder={<AppFormattedMessage id="VALUE" />}
                  shouldFormatNumber
                  wrapperClassName="w-[224px]"
                />
                <span className="text-xs text-gray-600">
                  <AppFormattedMessage id="AVAILABLE" />{' '}
                  <AnimatedNumber integer value={toPositive(available)} />
                </span>
              </div>
            </div>
          </div>
          <SubEventFileUpload
            clearErrors={clearErrors}
            companyId={companyId}
            errors={errors?.eventDetails?.grant?.grantItems?.[index]?.files || undefined}
            field={`stepOne.eventDetails.grant.grantItems.${index}.files`}
            files={stepOne?.eventDetails?.grant?.grantItems?.[index]?.files || []}
            formData={formData}
            linkedDocusignFileLinks={linkedDocusignFileLinks}
            setError={setError}
            setFormData={setFormData}
            text="grant"
          />
        </div>
      </FormDropDown>
      {hasDuplicates && firstDuplicateIndex === index && (
        <span className="rounded bg-fireside-50 p-3 text-xs font-[450] text-gray-700">
          <AppFormattedMessage id="GRANT_ASSOCIATION_INFO" />{' '}
          <span
            className="cursor-pointer font-[550] text-brand-700 underline"
            onClick={scrollToFirstDuplicate}
          >
            <AppFormattedMessage id="CLICK_HERE" />
          </span>
          .
        </span>
      )}
    </>
  );
};

export const GrantStepSecond: FC<GrantStepSecondProps> = ({
  control,
  setFormData,
  companyId,
  event,
  formData,
  setError,
  clearErrors,
  errors,
}) => {
  const [hasDuplicates, setHasDuplicates] = useState(false);
  const [firstDuplicateIndex, setFirstDuplicateIndex] = useState<null | number>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { messagesLocale } = useLocale();
  const { initialStep, stepOne } = useWatch<FormSchema>({ control });
  const signatureOption = stepOne?.eventDetails?.grant?.signatureOption;
  const { selectedCompany } = useSelectedCompany();
  const envelopeRemainingFromApi = selectedCompany?.docusignEnvelope?.remaining ?? 0;
  const selectedSharePlan = stepOne?.eventDetails?.grant?.sharePlan;
  const equityPlanId = selectedSharePlan?.id ?? '';

  const defaultDate = useMemo(() => new Date(+0), []);
  const watchedGrantItems = useWatch({
    control,
    name: 'stepOne.eventDetails.grant.grantItems',
  });

  const {
    fields: grantItemsFields,
    append: grantItemsAppend,
    remove: grantItemsRemove,
  } = useFieldArray({
    control,
    name: 'stepOne.eventDetails.grant.grantItems',
  });

  const grantItemCount = grantItemsFields.length;
  const isSignatureGrantFlow = signatureOption === GrantSignatureOption.SIGNATURE;
  const remainingEnvelopesAfterGrants = Math.max(0, envelopeRemainingFromApi - grantItemCount);
  const showRemainingEnvelopesChip = isSignatureGrantFlow && remainingEnvelopesAfterGrants <= 10;
  const isAddGrantDisabledByEnvelopes =
    isSignatureGrantFlow && envelopeRemainingFromApi - grantItemCount <= 0;

  const handleRemoveGrantItems = useCallback(
    (index: number) => {
      grantItemsRemove(index);
    },
    [grantItemsRemove],
  );

  const handleAppendGrantItems = useCallback(() => {
    const emptySharePlan = { id: '', name: '', pool: { id: '' } };
    const firstItemPlan = watchedGrantItems?.[0]?.sharePlan;
    const planSource =
      selectedSharePlan?.id != null && selectedSharePlan.id !== ''
        ? selectedSharePlan
        : firstItemPlan?.id != null && firstItemPlan.id !== ''
          ? firstItemPlan
          : null;
    const sharePlanToAppend =
      planSource != null
        ? {
            id: planSource.id ?? '',
            name: planSource.name ?? '',
            pool: { id: planSource.pool?.id ?? '' },
          }
        : emptySharePlan;
    grantItemsAppend({
      grantDate: defaultDate,
      sharePlan: sharePlanToAppend,
      stakeholder: { id: '', fullName: '' },
      shares: 0,
      balance: 0,
    });
  }, [defaultDate, grantItemsAppend, selectedSharePlan, watchedGrantItems]);

  const checkForDuplicates = useCallback(() => {
    const combinations = new Set<string>();
    let duplicatesFound = false;
    let firstDuplicateIdx: null | number = null;

    if (watchedGrantItems) {
      watchedGrantItems.forEach((field, index) => {
        const combination = `${field?.stakeholder?.id}-${field?.sharePlan?.id}`;
        if (combinations.has(combination) && field?.stakeholder?.id && field?.sharePlan?.id) {
          duplicatesFound = true;
          if (firstDuplicateIdx === null) {
            firstDuplicateIdx = index;
          }
        } else {
          combinations.add(combination);
        }
      });
    }

    setHasDuplicates(duplicatesFound);
    setFirstDuplicateIndex(firstDuplicateIdx);
  }, [watchedGrantItems]);

  const scrollToFirstDuplicate = useCallback(() => {
    if (firstDuplicateIndex !== null && itemRefs.current[firstDuplicateIndex]) {
      itemRefs.current[firstDuplicateIndex - 1]?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [firstDuplicateIndex]);

  const getFullTitle = useCallback(
    (index: number) => {
      const stakeholderName =
        stepOne?.eventDetails?.grant?.grantItems?.[index]?.stakeholder?.fullName;

      if (stakeholderName) {
        return `${messagesLocale['GRANT']} - ${stakeholderName}`;
      }

      return messagesLocale['GRANT'];
    },
    [messagesLocale, stepOne?.eventDetails?.grant?.grantItems],
  );

  useEffect(() => {
    if (
      initialStep?.type === EventFormType.GRANT &&
      grantItemsFields.length === 0 &&
      equityPlanId
    ) {
      handleAppendGrantItems();
    }
  }, [equityPlanId, grantItemsFields.length, handleAppendGrantItems, initialStep?.type]);

  useEffect(() => {
    checkForDuplicates();
  }, [checkForDuplicates, grantItemsFields]);

  return (
    <>
      {grantItemsFields.map((field, index) => (
        <GrantItem
          clearErrors={clearErrors}
          companyId={companyId}
          control={control}
          errors={errors}
          event={event}
          field={field}
          firstDuplicateIndex={firstDuplicateIndex}
          formData={formData}
          getFullTitle={getFullTitle}
          handleRemoveGrantItems={handleRemoveGrantItems}
          hasDuplicates={hasDuplicates}
          index={index}
          isShownRemoveButton={grantItemsFields.length > 1 && event === null}
          itemRefs={itemRefs}
          key={`${field}_${index}`}
          scrollToFirstDuplicate={scrollToFirstDuplicate}
          setError={setError}
          setFormData={setFormData}
        />
      ))}
      {event === null && (
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <Button
            className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent disabled:text-gray-400 disabled:no-underline"
            disabled={grantItemsFields.length >= 100 || isAddGrantDisabledByEnvelopes}
            disabledStyle={ButtonDisableStyle.NONE}
            onClick={handleAppendGrantItems}
            styleType="NONE"
          >
            + <AppFormattedMessage id="ADD_ANOTHER_GRANT" />
          </Button>
          {showRemainingEnvelopesChip && (
            <span
              className={
                remainingEnvelopesAfterGrants > 0
                  ? 'rounded-full bg-forest-50 px-3 py-1 text-xs font-[450] text-forest-600'
                  : 'rounded-full bg-fireside-50 px-3 py-1 text-xs font-[450] text-fireside-600'
              }
            >
              {remainingEnvelopesAfterGrants} <AppFormattedMessage id="REMAINING_ENVELOPES" />
            </span>
          )}
        </div>
      )}
      <DropDown
        answer={[
          messagesLocale['GRANT_EVENT_EXPLANATION_1'],
          messagesLocale['GRANT_EVENT_EXPLANATION_2'],
          messagesLocale['GRANT_EVENT_EXPLANATION_3'],
        ]}
        className="rounded-lg bg-gray-50"
        question={<AppFormattedMessage id="WHAT_IS_GRANT" />}
      />
    </>
  );
};
